import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router'
import { PageTurn, type Leaf } from '@/components/PageTurn'
import { useIsWide } from '@/lib/platform'
import type { Keeping } from '@/lib/print/design'
import { cn } from '@/lib/utils'

// What every print builder shares: the thing being printed beside the panel of choices (Frame); for a book, its
// pages turn (BookBuilder), and for recipe cards, they flip (CardDeck.tsx). A book is its faces in
// order, from the front cover to the back, each a page as it will be printed; `pages` is how many of them are the
// printed, numbered pages (the faces between the two inside covers). `thisPage` gives the choices for just the faces
// showing, by their place in `faces`, under the book.

export type Trim = { width: number; height: number }
export type Photos = 'color' | 'mono' | 'sepia'

export function BookBuilder({
  label,
  badge,
  back,
  trim,
  faces,
  pages,
  thisPage,
  photos,
  children,
}: {
  // The article's name, and the words in the pill at the top right.
  label: string
  badge: string
  back: { to: string; label: string }
  trim: Trim
  faces: (() => ReactNode)[]
  pages: number
  thisPage?: (shown: number[]) => ReactNode
  photos?: Photos
  children: ReactNode
}) {
  const wide = useIsWide()
  // Each face's place in `faces`. A spread needs an even number of faces: a blank page goes before the inside back
  // cover if not.
  const places = faces.map((_, i) => i)
  const all: (number | null)[] = wide && faces.length % 2 ? [...places.slice(0, -2), null, ...places.slice(-2)] : places
  const draw = (place: number | null | undefined) => (place == null ? null : faces[place]!())
  const leaves: Leaf[] = wide
    ? Array.from({ length: all.length / 2 }, (_, i) => ({
        front: draw(all[2 * i]),
        back: draw(all[2 * i + 1]),
        label: i === 0 ? 'Cover' : `Pages ${2 * i - 1} and ${2 * i}`,
      }))
    : all.map((place, i) => ({ front: draw(place), label: i === 0 ? 'Cover' : `Page ${i}` }))
  const [open, setOpen] = useState(0)
  const at = Math.min(open, leaves.length - 1)
  // The faces showing: the left and right of an open spread, or the one page.
  const shown = (wide ? [all[2 * at - 1], all[2 * at]] : [all[at]]).filter((x): x is number => x != null)

  return (
    <Frame
      label={label}
      heading="Print this book"
      badge={badge}
      back={back}
      photos={photos}
      stage={
        <section aria-label="The book" className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-20">
          <Stage trim={trim} spread={wide} roomBelow={Boolean(thisPage)}>
            <PageTurn leaves={leaves} open={at} onTurn={setOpen} spread={wide} className="kp-book" />
          </Stage>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              aria-label="Turn back"
              disabled={at === 0}
              onClick={() => setOpen(at - 1)}
              className={ROUND}
            >
              <ChevronLeft aria-hidden className="size-5" />
            </button>
            <p
              aria-live="polite"
              data-testid="book-where"
              className="min-w-40 text-center text-sm text-muted-foreground tabular-nums"
            >
              {where(at, leaves.length, wide, pages)}
            </p>
            <button
              type="button"
              aria-label="Turn the page"
              disabled={at === leaves.length - 1}
              onClick={() => setOpen(at + 1)}
              className={ROUND}
            >
              <ChevronRight aria-hidden className="size-5" />
            </button>
          </div>
          <p className="text-center text-xs text-muted-foreground">Drag a page, or tap its edge, to turn it.</p>
          {thisPage && <div className="grid gap-2 sm:grid-cols-2">{thisPage(shown)}</div>}
        </section>
      }
    >
      {children}
    </Frame>
  )
}

// A round button beside the book or the cards, to turn or flip them.
export const ROUND =
  'grid size-10 place-items-center rounded-full bg-background shadow-paper outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-40'

// The builder's whole screen: a close button and what's being printed along the top, then the thing itself beside
// the panel of choices.
export function Frame({
  label,
  heading,
  badge,
  back,
  stage,
  photos = 'color',
  children,
}: {
  label: string
  heading: string
  badge: string
  back: { to: string; label: string }
  stage: ReactNode
  // How the photos will print; the pages on screen show them that way too (index.css).
  photos?: Photos
  children: ReactNode
}) {
  return (
    <article
      aria-label={label}
      data-photos={photos}
      className="fixed inset-0 z-50 overflow-y-auto bg-table text-foreground"
    >
      <header className="sticky top-0 z-10 flex items-center gap-2 bg-table/85 px-3 pt-[calc(var(--safe-top)+0.5rem)] pb-2 backdrop-blur-md sm:px-5">
        <Link
          to={back.to}
          aria-label={`Close and go back to ${back.label}`}
          className="grid size-11 place-items-center rounded-full bg-background/80 shadow-paper transition-colors outline-none hover:bg-background focus-visible:ring-[3px] focus-visible:ring-ring/60"
        >
          <X aria-hidden />
        </Link>
        <h1 className="min-w-0 truncate text-base font-semibold">{heading}</h1>
        <p className="ml-auto rounded-full bg-background/85 px-3 py-1.5 text-xs font-semibold whitespace-nowrap shadow-paper">
          {badge}
        </p>
      </header>

      <div className="mx-auto grid max-w-7xl gap-5 px-4 pb-[calc(var(--safe-bottom)+2rem)] sm:px-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        {stage}
        <aside className="flex flex-col rounded-3xl bg-background shadow-paper">{children}</aside>
      </div>
    </article>
  )
}

function where(open: number, leaves: number, wide: boolean, pages: number) {
  if (open === 0) return 'Cover'
  if (open === leaves - 1 && wide) return 'Back cover'
  if (!wide)
    return open === leaves - 1
      ? 'Back cover'
      : open === 1 || open === leaves - 2
        ? 'Inside cover'
        : `Page ${open - 1} of ${pages}`
  const left = 2 * open - 2
  const right = 2 * open - 1
  const shown = [left, right].filter((n) => n >= 1 && n <= pages)
  return shown.length ? `${shown.length > 1 ? 'Pages' : 'Page'} ${shown.join('–')} of ${pages}` : 'Inside cover'
}

// The space the book sits in: the book is as large as fits, keeping the trim's shape.
function Stage({
  trim,
  spread,
  roomBelow,
  children,
}: {
  trim: Trim
  spread: boolean
  // Leave room under the book on a big screen for a page's own choices.
  roomBelow: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) =>
      setSize({ width: entry!.contentRect.width, height: entry!.contentRect.height }),
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const ratio = (trim.width / trim.height) * (spread ? 2 : 1)
  const width = Math.min(size.width, size.height * ratio)
  return (
    <div
      ref={ref}
      className={cn(
        'grid h-[min(58svh,44rem)] min-h-80 place-items-center',
        roomBelow ? 'lg:h-[calc(100svh-19rem)]' : 'lg:h-[calc(100svh-11rem)]',
      )}
    >
      <div
        style={
          {
            width,
            '--kp-page': `${trim.width} / ${trim.height}`,
            '--kp-spread': `${2 * trim.width} / ${trim.height}`,
          } as CSSProperties
        }
      >
        {width > 0 && children}
      </div>
    </div>
  )
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-t border-border px-5 py-5 first:border-t-0">
      <h2 className="eyebrow text-muted-foreground">{title}</h2>
      {children}
    </section>
  )
}

// A named look to start from, shown as this book's own page(s) in it.
export function LookTile({
  name,
  blurb,
  checked,
  onPick,
  children,
}: {
  name: string
  blurb: string
  checked: boolean
  onPick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={name}
      onClick={onPick}
      className={cn(
        'flex flex-col gap-2 rounded-2xl border border-border bg-secondary/60 p-2.5 text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60',
        checked && 'border-foreground bg-background ring-1 ring-foreground',
      )}
    >
      <span className="grid h-24 place-items-center" aria-hidden inert>
        {children}
      </span>
      <span>
        <span className="block text-sm font-semibold">{name}</span>
        <span className="block text-xs leading-snug text-muted-foreground">{blurb}</span>
      </span>
    </button>
  )
}

// One page's (or recipe's) own choices, under the book, with Reset once it has any.
export function PageEdit({
  name,
  edited,
  onReset,
  children,
}: {
  name: string
  edited: boolean
  onReset: () => void
  children: ReactNode
}) {
  return (
    <section aria-label={name} className="flex flex-col gap-3 rounded-2xl bg-background p-4 shadow-paper">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{name}</h2>
        {edited && (
          <button
            type="button"
            onClick={onReset}
            className="text-sub text-muted-foreground underline underline-offset-4"
          >
            Reset
          </button>
        )}
      </div>
      {children}
    </section>
  )
}

// What to do when no page showing can be changed on its own.
export const NoPageEdit = ({ children }: { children: ReactNode }) => (
  <p className="text-center text-sub text-muted-foreground sm:col-span-2">{children}</p>
)

// "Your look, started from …", with Reset, once any choice differs from the look started from.
export function YourLook({ from, onReset }: { from: string; onReset: () => void }) {
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 text-sub text-muted-foreground">
      <span>
        <span className="font-semibold text-foreground">Your look</span>, started from {from}
      </span>
      <button type="button" onClick={onReset} className="text-brand underline underline-offset-4">
        Reset
      </button>
    </p>
  )
}

// Where the design is kept: with the book, for its maker; on this device, for anyone else.
export function Kept({ mine, keeping, noun, by }: { mine: boolean; keeping: Keeping; noun: string; by: string }) {
  return (
    <p role="status" className="text-xs text-muted-foreground">
      {!mine
        ? `This starts from ${by ? `${by}’s` : 'the'} design. Your changes are just for you, on this device.`
        : keeping === 'failed'
          ? 'Your design couldn’t be saved. Check your connection, then change anything to try again.'
          : keeping === 'saving'
            ? 'Saving…'
            : `Your design is kept with the ${noun}, the same on any device.`}
    </p>
  )
}

// A row of choices, one picked.
export function Choice<T extends string | number | boolean>({
  label,
  hint,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string
  hint?: string
  value: T
  options: [T, string][]
  disabled?: boolean
  onChange: (value: T) => void
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <span id={id} className="text-sub text-muted-foreground">
        {label}
      </span>
      <div role="radiogroup" aria-labelledby={id} className="flex flex-wrap gap-0.5 rounded-xl bg-secondary p-1">
        {options.map(([v, text]) => (
          <button
            key={String(v)}
            type="button"
            role="radio"
            aria-checked={v === value}
            disabled={disabled}
            onClick={() => onChange(v)}
            className={cn(
              'h-8 flex-auto rounded-lg px-2.5 text-sm whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:opacity-50',
              v === value ? 'bg-background font-semibold shadow-sm' : 'hover:bg-background/60',
            )}
          >
            {text}
          </button>
        ))}
      </div>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  )
}
